import { useState, useEffect, useMemo } from 'react'
import { Download, FileText } from '@phosphor-icons/react'
import { api } from '../lib/api'

interface HandoutItem {
  moduleId: string
  title: string
  crew: string
  stage: string
  teams: string[]
  filename: string
}

const CREW_ORDER = ['all', 'audio', 'video', 'stage', 'it'] as const
const CREW_LABELS: Record<string, string> = {
  all: 'All Crews',
  audio: 'Audio',
  video: 'Video',
  stage: 'Stage',
  it: 'IT',
}

const STAGE_LABELS: Record<string, string> = {
  foundation: 'Foundation',
  core: 'Core',
  advanced: 'Advanced',
  enrichment: 'Enrichment',
}

function HandoutCard({ item }: { item: HandoutItem }) {
  const pdfUrl = `/handouts/${item.filename}.pdf`

  return (
    <div className="handout-card">
      <div className="handout-card__icon">
        <FileText size={24} weight="fill" />
      </div>
      <div className="handout-card__body">
        <h3 className="handout-card__title">{item.title}</h3>
        <div className="handout-card__meta">
          <span className={`badge badge--${item.crew}`}>{CREW_LABELS[item.crew] ?? item.crew}</span>
          <span className={`badge badge--stage-${item.stage}`}>{STAGE_LABELS[item.stage] ?? item.stage}</span>
        </div>
      </div>
      <a
        href={pdfUrl}
        download
        className="handout-card__action"
        title="Download PDF"
      >
        <Download size={20} weight="bold" />
      </a>
    </div>
  )
}

export default function ReferencePage() {
  const [handouts, setHandouts] = useState<HandoutItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [crewFilter, setCrewFilter] = useState<string>('all')

  useEffect(() => {
    async function load() {
      try {
        const [indexRes, manifestRes] = await Promise.all([
          api.getContentIndex(),
          fetch('/handouts/manifest.json').then((r) => r.json()),
        ])

        const modules = indexRes.modules ?? []
        const manifest: Record<string, string> = manifestRes

        const items: HandoutItem[] = []
        for (const mod of modules) {
          const filename = manifest[mod.id]
          if (!filename) continue
          items.push({
            moduleId: mod.id,
            title: mod.title,
            crew: mod.crew ?? 'all',
            stage: mod.stage ?? 'foundation',
            teams: mod.teams ?? [],
            filename,
          })
        }

        setHandouts(items)
      } catch (err) {
        console.error('Failed to load reference data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim()
    return handouts.filter((h) => {
      if (crewFilter !== 'all' && h.crew !== crewFilter) return false
      if (!q) return true
      return h.title.toLowerCase().includes(q) || h.moduleId.toLowerCase().includes(q)
    })
  }, [handouts, filter, crewFilter])

  const grouped = useMemo(() => {
    const map = new Map<string, HandoutItem[]>()
    for (const item of filtered) {
      const key = item.crew
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(item)
    }
    // Sort each group by stage priority then title
    const stagePriority: Record<string, number> = { foundation: 1, core: 2, advanced: 3, enrichment: 4 }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const pa = stagePriority[a.stage] ?? 99
        const pb = stagePriority[b.stage] ?? 99
        if (pa !== pb) return pa - pb
        return a.title.localeCompare(b.title)
      })
    }
    return map
  }, [filtered])

  if (loading) return <p>Loading reference materials…</p>
  if (error) return <p className="error">Error: {error}</p>

  return (
    <div className="reference-page">
      <header className="crew-header">
        <p className="eyebrow">Reference</p>
        <h1>Training Handouts</h1>
        <p className="lead">{handouts.length} printable PDFs — one per module.</p>
      </header>

      <div className="reference-controls">
        <input
          type="search"
          placeholder="Search handouts…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="reference-search"
        />
        <div className="reference-crew-filter">
          <button
            type="button"
            className={crewFilter === 'all' ? 'active' : ''}
            onClick={() => setCrewFilter('all')}
          >
            All
          </button>
          {CREW_ORDER.filter((c) => c !== 'all').map((c) => (
            <button
              key={c}
              type="button"
              className={crewFilter === c ? 'active' : ''}
              onClick={() => setCrewFilter(c)}
            >
              {CREW_LABELS[c]}
            </button>
          ))}
        </div>
      </div>

      <div className="reference-sections">
        {CREW_ORDER.map((crew) => {
          const items = grouped.get(crew)
          if (!items || items.length === 0) return null
          return (
            <section key={crew} className="reference-section">
              <h2>{CREW_LABELS[crew]}</h2>
              <div className="handout-grid">
                {items.map((item) => (
                  <HandoutCard key={item.moduleId} item={item} />
                ))}
              </div>
            </section>
          )
        })}
      </div>

      {filtered.length === 0 && (
        <p className="reference-empty">No handouts match your search.</p>
      )}
    </div>
  )
}
